import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Users, Crown, Sparkles, Lock, Euro, Download, Trash2, KeyRound, ShieldCheck, TrendingUp, UserPlus, FileUp, CheckCircle2, Undo2, History,
} from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="font-serif text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function fmtDiffValue(campo, val) {
  if (val === null || val === undefined) return "—";
  if (campo === "voci_codex" && Array.isArray(val)) {
    return val.map((v) => v.termine).join(", ");
  }
  const s = typeof val === "string" ? val : JSON.stringify(val);
  return s.length > 240 ? s.slice(0, 240) + "…" : s;
}

function FieldDiff({ campo, prima, dopo }) {
  return (
    <div className="text-xs">
      <p className="font-medium text-muted-foreground">{campo}</p>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div className="bg-destructive/5 border border-destructive/20 rounded-sm p-2">
          <p className="text-[10px] uppercase tracking-wide text-destructive/70 mb-0.5">Prima</p>
          <p className="whitespace-pre-wrap break-words text-foreground/80">{fmtDiffValue(campo, prima)}</p>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-2">
          <p className="text-[10px] uppercase tracking-wide text-primary/70 mb-0.5">Dopo</p>
          <p className="whitespace-pre-wrap break-words text-foreground/80">{fmtDiffValue(campo, dopo)}</p>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const emptyPriv = { name: "", email: "", password: "", tipo: "super", giorni: "365", plan: "annuale" };
  const [privOpen, setPrivOpen] = useState(false);
  const [privForm, setPrivForm] = useState(emptyPriv);
  const [importOpen, setImportOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importPayload, setImportPayload] = useState(null);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [backups, setBackups] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [retentionDays, setRetentionDays] = useState(30);
  const [creaMancanti, setCreaMancanti] = useState(false);
  const [courses, setCourses] = useState([]);
  const [expectedCourse, setExpectedCourse] = useState("");

  const load = useCallback(async () => {
    const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
    setStats(s.data);
    setUsers(u.data.users);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Errore nel caricamento")); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/users/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "iscritti_bottega.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  const cleanup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test");
      toast.success(`${data.deleted} account di test eliminati`);
      await load();
    } catch { toast.error("Errore pulizia"); }
    finally { setBusy(false); setCleanupOpen(false); }
  };

  const createPrivilege = async () => {
    const { name, email, password, tipo, giorni, plan } = privForm;
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error("Compila nome, email e password (min 6 caratteri)"); return;
    }
    const body = { name: name.trim(), email: email.trim(), password, tipo };
    if (tipo === "super") {
      const g = parseInt(giorni, 10);
      if (!g || g <= 0) { toast.error("Inserisci una durata valida in giorni"); return; }
      body.giorni = g;
    } else if (tipo === "normale") {
      body.plan = plan;
    }
    const label = { admin: "admin", super: "Super", normale: "Normale" }[tipo];
    setBusy(true);
    try {
      await api.post("/admin/users/privilege", body);
      toast.success(`Utente ${label} creato: ${email}`);
      setPrivOpen(false); setPrivForm(emptyPriv);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione utente"); }
    finally { setBusy(false); }
  };

  const confirmReset = async () => {    if (newPwd.length < 6) { toast.error("Minimo 6 caratteri"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password aggiornata per ${resetTarget.email}`);
      setResetTarget(null); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore reset"); }
    finally { setBusy(false); }
  };

  const openImport = () => {
    setImportFileName(""); setImportPayload(null); setImportError(""); setImportResult(null);
    setImportPreview(null); setCreaMancanti(false); setExpectedCourse("");
    setImportOpen(true);
    loadBackups();
    if (courses.length === 0) {
      api.get("/curriculum").then(({ data }) => setCourses(data.corsi.map((c) => ({ id: c.id, nome: c.nome })))).catch(() => {});
    }
  };

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/admin/import-backups");
      setBackups(data.backups);
    } catch { /* silenzioso */ }
  };

  const fetchPreview = async (payload) => {
    setPreviewing(true); setImportPreview(null);
    try {
      const { data } = await api.post("/admin/import-lectiones/preview", payload);
      setImportPreview(data);
    } catch (e) { setImportError(e.response?.data?.detail || "Errore anteprima"); }
    finally { setPreviewing(false); }
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    setImportError(""); setImportResult(null); setImportPayload(null); setImportFileName(""); setImportPreview(null);
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        if (!json.course_id || !Array.isArray(json.lectiones) || json.lectiones.length === 0) {
          setImportError("Il file deve contenere 'course_id' e un array 'lectiones' non vuoto."); return;
        }
        if (json.lectiones.some((l) => typeof l.index !== "number")) {
          setImportError("Ogni voce in 'lectiones' deve avere un 'index' numerico."); return;
        }
        setImportPayload(json);
        fetchPreview(json);
      } catch {
        setImportError("File JSON non valido.");
      }
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!importPayload) return;
    setBusy(true); setImportResult(null);
    try {
      const { data } = await api.post("/admin/import-lectiones", { ...importPayload, crea_mancanti: creaMancanti, expected_course_id: expectedCourse || null });
      setImportResult(data);
      const creaMsg = data.totale_create ? ` · ${data.totale_create} create` : "";
      toast.success(`Import '${data.course_id}': ${data.totale_modified}/${data.lezioni_totali} aggiornate${creaMsg}`);
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'import"); }
    finally { setBusy(false); }
  };

  const restoreBackup = async (batchId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/import-backups/${batchId}/restore`);
      toast.success(`Ripristinate ${data.ripristinate} lezioni di '${data.course_id}'`);
      loadBackups();
      if (historyOpen) loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ripristino"); }
    finally { setBusy(false); }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/import-history");
      setHistory(data.history);
      setRetentionDays(data.retention_days);
    } catch { toast.error("Errore nel caricamento dello storico"); }
  };

  const openHistory = () => {
    setHistory(null); setHistoryOpen(true); loadHistory();
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.email} eliminato`);
      setDeleteTarget(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
  const planBadge = (u) => {
    if (u.role === "admin") return <Badge className="rounded-full bg-foreground text-background">admin</Badge>;
    if (!u.has_access) return <Badge variant="outline" className="rounded-full text-muted-foreground">scaduto</Badge>;
    if (u.plan === "trial") return <Badge variant="outline" className="rounded-full border-accent/40"><Sparkles className="h-3 w-3 mr-1" />prova</Badge>;
    if (u.plan === "privilege") return <Badge className="rounded-full bg-accent text-accent-foreground"><ShieldCheck className="h-3 w-3 mr-1" />riservato</Badge>;
    return <Badge className="rounded-full bg-primary text-primary-foreground">{u.plan}</Badge>;
  };

  if (!stats || !users)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl font-bold">Pannello Admin</h1>
        </div>
        <p className="text-muted-foreground mb-8">Gestione iscritti, abbonamenti e progressi della Bottega.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard icon={Users} label="Iscritti" value={stats.students} sub={`${stats.total_users} totali`} />
          <StatCard icon={Crown} label="Abbonati attivi" value={stats.active_subscribers} sub={`${stats.mecenati} mecenati`} />
          <StatCard icon={Sparkles} label="In prova" value={stats.trial_users} sub={`${stats.expired_users} scaduti`} />
          <StatCard icon={Euro} label="Ricavi" value={`€${stats.revenue_eur.toFixed(2).replace(".", ",")}`} sub={`${stats.paid_count} pagamenti`} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={TrendingUp} label="Conversione" value={`${stats.conversion_pct}%`} sub="iscritti → paganti" />
          {Object.entries(stats.by_plan).map(([k, v]) => (
            <StatCard key={k} icon={Euro} label={`Piano ${k}`} value={v} sub="acquisti" />
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <Button onClick={() => { setPrivForm(emptyPriv); setPrivOpen(true); }} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="admin-create-user-btn">
            <UserPlus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Crea utente
          </Button>
          <Button onClick={exportCsv} variant="outline" className="rounded-sm" data-testid="admin-export-btn">
            <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Esporta CSV
          </Button>
          <Button onClick={openImport} variant="outline" className="rounded-sm" data-testid="admin-import-btn">
            <FileUp className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Importa Lezioni
          </Button>
          <Button onClick={openHistory} variant="outline" className="rounded-sm" data-testid="admin-history-btn">
            <History className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Storico import
          </Button>
          <Button onClick={() => setCleanupOpen(true)} variant="outline" disabled={busy} className="rounded-sm text-destructive hover:text-destructive" data-testid="admin-cleanup-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Pulisci account test
          </Button>
        </div>

        <div className="border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Utente</th>
                <th className="px-3 py-3">Tipo</th>
                <th className="px-3 py-3">Scadenza</th>
                <th className="px-3 py-3 text-center">Lectiones</th>
                <th className="px-3 py-3 text-center">Prove</th>
                <th className="px-3 py-3 text-center">Maestria</th>
                <th className="px-3 py-3 text-center">€</th>
                <th className="px-3 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="px-4 py-3">
                    <p className="font-medium">{u.name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="px-3 py-3">{planBadge(u)} {u.mecenate && <Crown className="inline h-3.5 w-3.5 text-accent ml-1" />}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{fmtDate(u.access_until)}</td>
                  <td className="px-3 py-3 text-center tabular-nums">{u.lectiones_completate}</td>
                  <td className="px-3 py-3 text-center tabular-nums">{u.prove_superate}</td>
                  <td className="px-3 py-3 text-center tabular-nums">{u.maestria.toLocaleString("it-IT")}</td>
                  <td className="px-3 py-3 text-center tabular-nums">{u.speso_eur ? `€${u.speso_eur}` : "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Reset password"
                        onClick={() => { setResetTarget(u); setNewPwd(""); }} data-testid={`admin-reset-${u.id}`}>
                        <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      {u.role !== "admin" && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" title="Elimina"
                          onClick={() => setDeleteTarget(u)} data-testid={`admin-delete-${u.id}`}>
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conferma pulizia account di test */}
      <AlertDialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pulire gli account di test?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati definitivamente tutti gli account con email che inizia per
              test_/tmp_/pass_/sub_ e i relativi dati. Operazione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={cleanup} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="cleanup-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Elimina account test
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conferma eliminazione singolo utente */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo utente?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `${deleteTarget.email} e tutti i suoi dati (progressi, prove, pagamenti) verranno eliminati definitivamente.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPwd(""); } }}>
        <DialogContent data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              {resetTarget && `Imposta una nuova password per ${resetTarget.email} (minimo 6 caratteri).`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-pwd">Nuova password</Label>
            <Input id="new-pwd" type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Almeno 6 caratteri" className="rounded-sm" data-testid="reset-pwd-input"
              onKeyDown={(e) => { if (e.key === "Enter") confirmReset(); }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPwd(""); }}
              className="rounded-sm" data-testid="reset-cancel-btn">Annulla</Button>
            <Button onClick={confirmReset} disabled={busy || newPwd.length < 6}
              className="rounded-sm bg-primary hover:bg-primary/90" data-testid="reset-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Aggiorna password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Importa lezioni (update mirato contenuti) */}
      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) setImportOpen(false); }}>
        <DialogContent data-testid="import-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importa Lezioni</DialogTitle>
            <DialogDescription>
              Carica un file JSON per aggiornare i testi delle lezioni di un corso. Aggiorna solo
              content, citazione, citazione_autore, voci_codex e argomento. Non tocca mai
              password, timeline o immagini.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="import-expected-course">Corso di destinazione atteso (facoltativo)</Label>
              <select id="import-expected-course" value={expectedCourse}
                onChange={(e) => setExpectedCourse(e.target.value)}
                className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm"
                data-testid="import-expected-course">
                <option value="">— Nessuna verifica —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome} ({c.id})</option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">Seleziona il corso che ti aspetti di aggiornare: se il file appartiene a un corso diverso, l'import viene bloccato.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="import-file">File JSON</Label>
              <Input id="import-file" type="file" accept="application/json,.json"
                onChange={onImportFile} className="rounded-sm" data-testid="import-file-input" />
              {importFileName && <p className="text-xs text-muted-foreground">Selezionato: {importFileName}</p>}
            </div>
            {importError && (
              <p className="text-sm text-destructive" data-testid="import-error">{importError}</p>
            )}
            {importPayload && !importError && expectedCourse && expectedCourse !== importPayload.course_id && (
              <div className="text-sm bg-destructive/10 border border-destructive/40 rounded-sm p-3 text-destructive" data-testid="import-course-mismatch">
                ⚠️ <span className="font-medium">Corso non corrispondente.</span> Il file è per <span className="font-mono">«{importPayload.course_id}»</span> ma hai selezionato <span className="font-mono">«{expectedCourse}»</span>. Import bloccato — cambia file o selezione.
              </div>
            )}
            {importPayload && !importError && (
              <div className="text-sm bg-secondary/50 border border-border rounded-sm p-3" data-testid="import-preview">
                <p>Corso rilevato dal file: <span className="font-medium">{(courses.find((c) => c.id === importPayload.course_id)?.nome) || importPayload.course_id}</span> <span className="font-mono text-xs text-muted-foreground">({importPayload.course_id})</span></p>
                <p>Lezioni nel file: <span className="font-medium">{importPayload.lectiones.length}</span> (index: {importPayload.lectiones.map((l) => l.index).join(", ")})</p>
              </div>
            )}

            {previewing && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="import-preview-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Calcolo anteprima prima/dopo…
              </p>
            )}

            {importPreview && !importResult && (
              <div className="space-y-3" data-testid="import-diff">
                {importPreview.dettaglio.some((d) => !d.trovata && d.creabile) && (
                  <label className="flex items-start gap-2 text-sm bg-accent/10 border border-accent/30 rounded-sm p-2.5 cursor-pointer" data-testid="import-crea-mancanti-label">
                    <input type="checkbox" checked={creaMancanti}
                      onChange={(e) => setCreaMancanti(e.target.checked)}
                      className="mt-0.5" data-testid="import-crea-mancanti" />
                    <span>
                      <span className="font-medium">Crea le lezioni non ancora presenti</span>
                      <span className="block text-xs text-muted-foreground">Alcune lezioni non esistono ancora nel database (vengono generate alla prima apertura). Attiva per crearle ora con i testi del file.</span>
                    </span>
                  </label>
                )}
                {importPreview.non_trovati.length > 0 && !creaMancanti && (
                  <p className="text-xs text-destructive">
                    ⚠️ Index non presenti nel corso (verranno ignorati): {importPreview.non_trovati.join(", ")}
                  </p>
                )}
                <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                  {importPreview.dettaglio.map((d) => {
                    const changed = d.campi.filter((c) => c.cambiato);
                    const willCreate = !d.trovata && d.creabile && creaMancanti;
                    return (
                      <div key={d.index} className="border border-border rounded-sm p-2.5" data-testid={`import-diff-${d.index}`}>
                        <p className="text-xs font-semibold mb-1.5">
                          Lezione index {d.index}
                          {d.trovata && d.titolo_attuale && (
                            <span className="text-muted-foreground font-normal italic"> → «{d.titolo_attuale}»</span>
                          )}
                          {!d.trovata && d.titolo_curriculum && (
                            <span className="text-muted-foreground font-normal italic"> → «{d.titolo_curriculum}»</span>
                          )}
                          {willCreate && <span className="text-primary ml-1">— NUOVA, verrà creata ({d.campi_nuovi.join(", ")})</span>}
                          {!d.trovata && !willCreate && d.creabile && <span className="text-destructive ml-1">— assente (abilita "Crea lezioni")</span>}
                          {!d.trovata && !d.creabile && <span className="text-destructive ml-1">— index non valido per questo corso</span>}
                          {d.trovata && changed.length === 0 && <span className="text-muted-foreground ml-1">— nessuna modifica</span>}
                          {d.trovata && changed.length > 0 && <span className="text-primary ml-1">— {changed.length} campo/i cambiano</span>}
                        </p>
                        <div className="space-y-2">
                          {changed.map((c) => (
                            <FieldDiff key={c.campo} campo={c.campo} prima={c.prima} dopo={c.dopo} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {importResult && (
              <div className="text-sm border border-border rounded-sm p-3 space-y-1" data-testid="import-result">
                <p className="flex items-center gap-1.5 font-medium text-primary">
                  <CheckCircle2 className="h-4 w-4" /> Import completato — {importResult.totale_modified}/{importResult.lezioni_totali} modificate{importResult.totale_create ? ` · ${importResult.totale_create} create` : ""}
                </p>
                {importResult.batch_id && (
                  <p className="text-xs text-muted-foreground">Backup salvato · puoi annullare qui sotto in "Ripristino"</p>
                )}
                <div className="max-h-40 overflow-y-auto text-xs font-mono">
                  {importResult.dettaglio.map((d) => (
                    <div key={d.index}>
                      index {d.index}: {d.creata ? "CREATA" : `matched=${d.matched} modified=${d.modified}`} [{d.campi.join(", ")}]{d.non_trovata ? " (ignorata: non presente)" : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {backups.length > 0 && (
              <div className="border-t border-border pt-3" data-testid="import-backups">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Ripristino (backup recenti)</p>
                <div className="max-h-40 overflow-y-auto space-y-1.5">
                  {backups.map((b) => (
                    <div key={b.batch_id} className="flex items-center justify-between gap-2 text-xs bg-secondary/40 rounded-sm px-2.5 py-1.5" data-testid={`backup-${b.batch_id}`}>
                      <div className="min-w-0">
                        <span className="font-medium">{b.course_id}</span>
                        <span className="text-muted-foreground"> · {b.lezioni.length} lez. · {new Date(b.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      {b.ripristinato ? (
                        <Badge variant="outline" className="rounded-full text-[10px] shrink-0">ripristinato</Badge>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy}
                          className="h-7 rounded-sm text-xs shrink-0"
                          onClick={() => restoreBackup(b.batch_id)}
                          data-testid={`backup-restore-${b.batch_id}`}>
                          <Undo2 className="h-3 w-3 mr-1" /> Annulla
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}
              className="rounded-sm" data-testid="import-close-btn">Chiudi</Button>
            <Button onClick={runImport} disabled={busy || !importPayload || !!importError || (expectedCourse && importPayload && expectedCourse !== importPayload.course_id)}
              className="rounded-sm bg-primary hover:bg-primary/90" data-testid="import-run-btn">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Applica import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storico import */}
      <Dialog open={historyOpen} onOpenChange={(o) => { if (!o) setHistoryOpen(false); }}>
        <DialogContent data-testid="history-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Storico import</DialogTitle>
            <DialogDescription>
              Tracciabilità completa degli import: data, corso e lezioni modificate.
              Gli snapshot di backup si conservano {retentionDays} giorni (poi l'import resta nello storico ma non è più annullabile).
            </DialogDescription>
          </DialogHeader>
          {!history && (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
          {history && history.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="history-empty">Nessun import registrato finora.</p>
          )}
          {history && history.length > 0 && (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.batch_id} className="border border-border rounded-sm p-3" data-testid={`history-${h.batch_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {h.course_id}
                        <span className="text-muted-foreground font-normal"> · {h.totale_modified} lezioni modificate</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {h.admin_email && ` · ${h.admin_email}`} · index {h.lezioni.join(", ")}
                      </p>
                    </div>
                    {h.ripristinato ? (
                      <Badge variant="outline" className="rounded-full text-[10px] shrink-0">ripristinato</Badge>
                    ) : h.backup_disponibile ? (
                      <Button size="sm" variant="outline" disabled={busy}
                        className="h-7 rounded-sm text-xs shrink-0"
                        onClick={() => restoreBackup(h.batch_id)}
                        data-testid={`history-restore-${h.batch_id}`}>
                        <Undo2 className="h-3 w-3 mr-1" /> Annulla
                      </Button>
                    ) : (
                      <Badge variant="outline" className="rounded-full text-[10px] shrink-0 text-muted-foreground">backup scaduto</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="rounded-sm" data-testid="history-close-btn">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crea utente con privilegi */}
      <Dialog open={privOpen} onOpenChange={(o) => { if (!o) { setPrivOpen(false); setPrivForm(emptyPriv); } }}>        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Crea utente</DialogTitle>
            <DialogDescription>
              Admin (accesso pieno, sine die) · Super (lectiones sbloccate, durata in giorni) · Normale (piano a pagamento).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="priv-name">Nome</Label>
              <Input id="priv-name" value={privForm.name} onChange={(e) => setPrivForm({ ...privForm, name: e.target.value })}
                placeholder="Nome e cognome" className="rounded-sm" data-testid="create-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="priv-email">Email</Label>
              <Input id="priv-email" type="email" value={privForm.email} onChange={(e) => setPrivForm({ ...privForm, email: e.target.value })}
                placeholder="email@esempio.it" className="rounded-sm" data-testid="create-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="priv-pwd">Password</Label>
              <Input id="priv-pwd" type="text" value={privForm.password} onChange={(e) => setPrivForm({ ...privForm, password: e.target.value })}
                placeholder="Almeno 6 caratteri" className="rounded-sm" data-testid="create-user-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="priv-tipo">Tipo di account</Label>
              <select id="priv-tipo" value={privForm.tipo} onChange={(e) => setPrivForm({ ...privForm, tipo: e.target.value })}
                className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="create-user-tipo">
                <option value="super">Super — lectiones sbloccate, no pannello</option>
                <option value="normale">Normale — piano a pagamento</option>
                <option value="admin">Admin — accesso pieno sine die + pannello</option>
              </select>
            </div>
            {privForm.tipo === "super" && (
              <div className="space-y-1.5">
                <Label htmlFor="priv-giorni">Durata accesso (giorni)</Label>
                <Input id="priv-giorni" type="number" min="1" value={privForm.giorni}
                  onChange={(e) => setPrivForm({ ...privForm, giorni: e.target.value })}
                  placeholder="es. 365" className="rounded-sm" data-testid="create-user-giorni" />
              </div>
            )}
            {privForm.tipo === "normale" && (
              <div className="space-y-1.5">
                <Label htmlFor="priv-plan">Piano</Label>
                <select id="priv-plan" value={privForm.plan} onChange={(e) => setPrivForm({ ...privForm, plan: e.target.value })}
                  className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="create-user-plan">
                  <option value="mensile">Mensile (30 giorni)</option>
                  <option value="annuale">Annuale (365 giorni)</option>
                  <option value="mecenate">Mecenate (365 giorni)</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPrivOpen(false); setPrivForm(emptyPriv); }}
              className="rounded-sm" data-testid="create-user-cancel">Annulla</Button>
            <Button onClick={createPrivilege} disabled={busy}
              className="rounded-sm bg-primary hover:bg-primary/90" data-testid="create-user-submit">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Crea utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}